import React, { useState, useEffect, useContext } from 'react';
import { Gift, Heart, ExternalLink, Loader, QrCode } from 'lucide-react';
import { LanguageContext } from '../LanguageContext';
import { GAPP_SCRIPT_API_KEY, GAPP_SCRIPT_URL } from '../constants/constants';
import './Registry.css';
import Footer from './Footer';

interface WishlistItem {
    id: string;
    name: string;
    link?: string;
    image?: string;
    descriptionRo?: string;
    descriptionEn?: string;
    cost: number;
    contribution: number;
    pendingContribution: number;
    remaining: number;
    percentageFunded: number;
    percentagePending: number;
    contributors: number;
}

interface ApiResponse {
    status: string;
    timestamp: string;
    items: {
        id: string;
        name: string;
        link: string;
        image: string;
        descriptionRo: string;
        descriptionEn: string;
        cost: number;
        contribution: number;
        pendingContribution: number;
        remaining: number;
        percentageFunded: number;
        percentagePending: number;
        contributors: number;
    }[];
}

const Registry: React.FC = () => {
    const [items, setItems] = useState<WishlistItem[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [contributionAmount, setContributionAmount] = useState<number>(0);
    const [contributorName, setContributorName] = useState<string>('');
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isQrModalOpen, setIsQrModalOpen] = useState<boolean>(false);
    const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
    const [submitLoading, setSubmitLoading] = useState<boolean>(false);
    const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
    const { language } = useContext(LanguageContext);

    // Translations
    const translations = {
        en: {
            title: 'Our Wedding Registry',
            subtitle: 'We\'re thankful for your presence on our special day. If you\'d like to help us start our new life together, here are some gifts we\'d love.',
            all: 'All Gifts',
            contributed: 'Contributed',
            contribute: 'Contribute',
            contributorsLabel: 'Contributors:',
            noItems: 'No items match your filter. Try a different filter.',
            addGiftTitle: 'Suggest a Gift',
            addGiftPlaceholder: 'Enter gift suggestion',
            add: 'Add',
            modalTitle: 'Contribute to this Gift',
            modalText: 'Please enter your name to be added to the contributors list.',
            namePlaceholder: 'Your name',
            contributionInfoText: 'After clicking Confirm, you\'ll see a QR code for payment via Revolut.',
            cancel: 'Cancel',
            confirm: 'Confirm',
            loading: 'Loading wedding registry...',
            error: 'Error loading registry. Please refresh the page.',
            viewDetails: 'View Details',
            contributeLabel: 'Contribution Amount',
            remaining: 'Remaining:',
            funded: 'Funded:',
            pendingLabel: 'Pending:',
            currency: 'EUR',
            done: 'Done',
            scanQrCode: 'Scan this QR code with your Revolut app to complete the payment.',
            dontHaveRevolut: 'Don\'t have Revolut?',
            usePaymentLink: 'You can use this payment link - no Revolut account required:',
            currencyChange: 'You can change the currency to your preferred one during the payment process.',
            paymentLink: 'Payment Link',
            submittingContribution: 'Recording your contribution...',
            contributionSuccess: 'Thank you! Your contribution has been recorded.',
            contributionError: 'There was an error recording your contribution. Please try again later.'
        },
        ro: {
            title: 'Lista noastră de cadouri',
            subtitle: 'Suntem recunoscători pentru prezența voastră în ziua noastră specială. Dacă doriți să ne ajutați să ne începem noua viață împreună, iată câteva cadouri pe care le-am dori.',
            all: 'Toate cadourile',
            contributed: 'Cu contribuții',
            contribute: 'Contribuie',
            contributorsLabel: 'Contribuitori:',
            noItems: 'Niciun element nu corespunde filtrului. Încercați un alt filtru.',
            addGiftTitle: 'Sugerează un cadou',
            addGiftPlaceholder: 'Introduceți sugestia de cadou',
            add: 'Adaugă',
            modalTitle: 'Contribuie la acest cadou',
            modalText: 'Vă rugăm să introduceți numele dvs. pentru a fi adăugat la lista de contribuitori.',
            namePlaceholder: 'Numele dvs.',
            contributionInfoText: 'După ce faceți clic pe Confirmă, veți vedea un cod QR pentru plată prin Revolut.',
            cancel: 'Anulează',
            confirm: 'Confirmă',
            loading: 'Se încarcă lista de cadouri...',
            error: 'Eroare la încărcarea listei. Vă rugăm reîmprospătați pagina.',
            viewDetails: 'Vezi detalii',
            contributeLabel: 'Suma contribuției',
            remaining: 'Rămas:',
            funded: 'Finanțat:',
            pendingLabel: 'În așteptare:',
            currency: 'EUR',
            done: 'Gata',
            scanQrCode: 'Scanați acest cod QR cu aplicația Revolut pentru a finaliza plata.',
            dontHaveRevolut: 'Nu aveți Revolut?',
            usePaymentLink: 'Puteți folosi acest link de plată - nu este necesar un cont Revolut:',
            currencyChange: 'Puteți schimba moneda în cea preferată în timpul procesului de plată.',
            paymentLink: 'Link de plată',
            submittingContribution: 'Se înregistrează contribuția...',
            contributionSuccess: 'Mulțumim! Contribuția dvs. a fost înregistrată.',
            contributionError: 'A apărut o eroare la înregistrarea contribuției. Vă rugăm să încercați din nou mai târziu.'
        }
    };

    const t = translations[language as keyof typeof translations];

    // Fetch registry data from API
    useEffect(() => {
        const fetchRegistryData = async () => {
            setLoading(true);
            try {
                const response = await fetch(GAPP_SCRIPT_URL);

                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }

                const data: ApiResponse = await response.json();

                if (data.status === 'success') {
                    // Set the items directly from the API, keeping both description fields
                    setItems(data.items);
                } else {
                    throw new Error('API returned an error');
                }
            } catch (err) {
                console.error('Error fetching registry data:', err);
                setError('Failed to load registry data');

                // Fall back to session storage if available
                const storedItems = sessionStorage.getItem('wishlistItems');
                if (storedItems) {
                    setItems(JSON.parse(storedItems));
                }
            } finally {
                setLoading(false);
            }
        };

        fetchRegistryData();
    }, []);;

    // Save items to session storage whenever they change
    useEffect(() => {
        if (items.length > 0) {
            sessionStorage.setItem('wishlistItems', JSON.stringify(items));
        }
    }, [items]);

    const openContributeModal = (id: string) => {
        setSelectedItemId(id);
        setIsModalOpen(true);

        // Reset contribution amount to default
        const selectedItem = items.find(item => item.id === id);
        if (selectedItem) {
            setContributionAmount(selectedItem.remaining || 100);
        } else {
            setContributionAmount(100);
        }
    };

    const confirmContribution = async () => {
        if (selectedItemId && contributorName.trim()) {
            try {
                // Note: We no longer update local state here - will update after QR code modal closes

                // Close the name input modal and open QR code modal
                setIsModalOpen(false);
                setIsQrModalOpen(true);
            } catch (err) {
                console.error('Error updating item:', err);
                // Handle error appropriately (e.g., show error message)
            }
        }
    };

    const submitContribution = async () => {
        if (!selectedItemId || !contributorName.trim() || contributionAmount <= 0) {
            return;
        }

        setSubmitLoading(true);
        setSubmitSuccess(false);

        try {
            // Send contribution to API with CORS mode
            const response = await fetch(GAPP_SCRIPT_URL, {
                method: 'POST',
                redirect: "follow",
                headers: { 'Content-Type': 'text/plain;charset=utf-8' },
                body: JSON.stringify({
                    itemId: selectedItemId,
                    contributor: contributorName.trim(),
                    amount: contributionAmount,
                    method: "saveContribution",
                    apiKey: GAPP_SCRIPT_API_KEY
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();

            if (data.status === 'success') {
                // Now update the local state after successful submission
                const selectedItem = items.find(item => item.id === selectedItemId);

                if (selectedItem) {
                    // Calculate new values for local state update
                    const newPendingContribution = selectedItem.pendingContribution + contributionAmount;
                    const newRemaining = Math.max(0, selectedItem.cost - selectedItem.contribution - newPendingContribution);

                    // Calculate new percentage pending - if over 100%, we'll still display it correctly
                    const newPercentagePending = Math.min(
                        100 - selectedItem.percentageFunded,
                        Math.round((newPendingContribution / selectedItem.cost) * 100)
                    );

                    // Update local state with pending contribution info
                    setItems(items.map(item =>
                        item.id === selectedItemId
                            ? {
                                ...item,
                                pendingContribution: newPendingContribution,
                                remaining: newRemaining,
                                percentagePending: newPercentagePending
                            }
                            : item
                    ));
                }

                setSubmitSuccess(true);
            } else {
                throw new Error(data.message || 'Unknown error');
            }
        } catch (err) {
            console.error('Error submitting contribution:', err);
            setSubmitSuccess(false);
        } finally {
            setSubmitLoading(false);
        }
    };

    const closeQrModal = async () => {
        // Submit the contribution when closing the QR code modal
        if (!submitSuccess && !submitLoading) {
            await submitContribution();
        }

        // Small delay to allow user to see success message
        setTimeout(() => {
            setIsQrModalOpen(false);
            setContributorName('');
            setSelectedItemId(null);
            setContributionAmount(0);
            setSubmitSuccess(false);
        }, submitSuccess ? 1500 : 0);
    };

    const getDescription = (item: WishlistItem): string | undefined => {
        if (language === 'ro') {
            return item.descriptionRo || item.descriptionEn; // Fallback to English if Romanian is missing
        }
        return item.descriptionEn || item.descriptionRo; // Fallback to Romanian if English is missing
    };

    if (loading) {
        return (
            <div className="registry-container min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <Loader className="animate-spin h-8 w-8 mx-auto mb-4 text-gray-600" />
                    <p className="text-gray-700 font-serif">{t.loading}</p>
                </div>
            </div>
        );
    }

    if (error && items.length === 0) {
        return (
            <div className="registry-container min-h-screen flex items-center justify-center">
                <div className="text-center text-red-600 font-serif">
                    <p>{t.error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="registry-container min-h-screen pt-20 pb-10">
            <div className="max-w-5xl mx-auto p-6 bg-white rounded-lg shadow-md">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-32 h-32 opacity-20">
                    <img src="/images/flowers.svg" alt="" className="w-full h-full object-contain" />
                </div>
                <div className="absolute bottom-0 left-0 w-32 h-32 opacity-20">
                    <img src="/images/flowers.svg" alt="" className="w-full h-full object-contain scale-x-[-1]" />
                </div>

                <div className="text-center mb-8 relative">
                    <div className="mb-4">
                        <Gift className="inline-block w-8 h-8 text-gray-800" />
                    </div>
                    <h1 className="text-3xl font-serif text-gray-800 mb-4">{t.title}</h1>
                    <div className="w-16 h-1 bg-gray-300 mx-auto mb-4"></div>
                    <p className="text-gray-700 max-w-xl mx-auto">
                        {t.subtitle}
                    </p>
                </div>

                {/* Decorative image */}
                <div className="flex justify-center mb-8">
                    <div className="relative w-32 h-16">
                        <img src="/images/wine_glass.svg" alt="" className="absolute left-0 top-0 h-full opacity-70" />
                        <img src="/images/flowers.svg" alt="" className="absolute left-1/2 top-0 transform -translate-x-1/2 h-full opacity-70" />
                        <img src="/images/martini-glass.svg" alt="" className="absolute right-0 top-0 h-full opacity-70" />
                    </div>
                </div>

                {/* Item list - revised for new data structure with pending contributions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                    {items.length > 0 ? (
                        items.map((item) => (
                            <div
                                key={item.id}
                                className="border rounded-lg overflow-hidden transition-all registry-item border-gray-200 bg-white hover:border-gray-300"
                            >
                                {item.image && (
                                    <div className="h-48 overflow-hidden">
                                        <img
                                            src={item.image}
                                            alt={item.name}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                )}
                                <div className="p-5">
                                    <div className="flex justify-between mb-2">
                                        <h3 className="font-serif text-lg text-gray-800">
                                            {item.name}
                                        </h3>
                                        {item.contributors > 0 && (
                                            <div className="flex items-center text-xs text-gray-500">
                                                <Heart className="w-3 h-3 mr-1 text-gray-400" />
                                                {`${t.contributorsLabel} ${item.contributors}`}
                                            </div>
                                        )}
                                    </div>

                                    {getDescription(item) && (
                                        <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                                            {getDescription(item)}
                                        </p>
                                    )}

                                    {/* Progress bar showing both confirmed and pending contributions */}
                                    <div className="mb-3">
                                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                                            <div>
                                                <span>{t.funded} {Math.round(item.percentageFunded)}%</span>
                                                {item.percentagePending > 0 && (
                                                    <span className="ml-2 text-amber-600">
                                                        {t.pendingLabel} {Math.round(item.percentagePending)}%
                                                    </span>
                                                )}
                                            </div>
                                            <span>
                                                {t.remaining} {t.currency} {item.remaining}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden relative">
                                            {/* Confirmed contributions */}
                                            <div
                                                className="bg-gray-800 h-2 rounded-l-full absolute"
                                                style={{ width: `${item.percentageFunded}%` }}
                                            ></div>
                                            {/* Pending contributions */}
                                            {item.percentagePending > 0 && (
                                                <div
                                                    className="bg-amber-500 h-2 absolute"
                                                    style={{
                                                        width: `${item.percentagePending}%`,
                                                        left: `${item.percentageFunded}%`
                                                    }}
                                                ></div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap items-center justify-between gap-2 mt-4">
                                        <div className="flex space-x-2">
                                            {item.link && (
                                                <a
                                                    href={item.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-sm bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded-full hover:bg-gray-50 transition-colors duration-300 flex items-center"
                                                >
                                                    {t.viewDetails}
                                                    <ExternalLink className="w-3 h-3 ml-1" />
                                                </a>
                                            )}

                                            <button
                                                onClick={() => openContributeModal(item.id)}
                                                className="text-sm bg-gray-800 text-white px-3 py-1.5 rounded-full hover:bg-gray-700 transition-colors duration-300 flex items-center"
                                                aria-label={`Contribute to ${item.name}`}
                                            >
                                                {t.contribute}
                                                <QrCode className="w-3 h-3 ml-1" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="col-span-2 text-center text-gray-500 py-12 font-serif italic">
                            {t.noItems}
                        </div>
                    )}
                </div>

                <Footer/>
            </div>

            {/* Contribution Modal */}
            {isModalOpen && selectedItemId && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md relative">
                        {/* Decorative element */}
                        <div className="absolute top-0 right-0 w-16 h-16 opacity-20">
                            <img src="/images/flowers.svg" alt="" className="w-full h-full object-contain" />
                        </div>

                        <h3 className="text-xl font-serif text-gray-800 mb-4">
                            {t.modalTitle}
                        </h3>

                        {/* Item details */}
                        {(() => {
                            const item = items.find(item => item.id === selectedItemId);
                            if (!item) return null;

                            return (
                                <div className="mb-6">
                                    <p className="text-lg font-serif text-gray-700 mb-2">{item.name}</p>
                                    {getDescription(item) && (
                                        <p className="text-sm text-gray-600 mb-4">{getDescription(item)}</p>
                                    )}
                                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                                        <div>
                                            <span>{t.funded}: {Math.round(item.percentageFunded)}%</span>
                                            {item.percentagePending > 0 && (
                                                <span className="ml-2 text-amber-600">
                                                    {t.pendingLabel} {Math.round(item.percentagePending)}%
                                                </span>
                                            )}
                                        </div>
                                        <span>{t.currency} {item.cost}</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2 mb-4 overflow-hidden relative">
                                        {/* Confirmed contributions */}
                                        <div
                                            className="bg-gray-800 h-2 absolute"
                                            style={{ width: `${item.percentageFunded}%` }}
                                        ></div>
                                        {/* Pending contributions */}
                                        {item.percentagePending > 0 && (
                                            <div
                                                className="bg-amber-500 h-2 absolute"
                                                style={{
                                                    width: `${item.percentagePending}%`,
                                                    left: `${item.percentageFunded}%`
                                                }}
                                            ></div>
                                        )}
                                    </div>
                                </div>
                            );
                        })()}

                        <p className="text-gray-600 mb-4 font-serif">
                            {t.contributionInfoText}
                        </p>

                        <div className="mb-4">
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                {t.contributeLabel}
                            </label>
                            <input
                                type="number"
                                min="1"
                                value={contributionAmount || ""}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setContributionAmount(value === "" ? 0 : Number(value));
                                }}
                                className="w-full border border-gray-300 rounded-lg px-4 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-gray-400 font-serif"
                            />
                        </div>

                        <p className="text-gray-600 mb-6 font-serif">
                            {t.modalText}
                        </p>
                        <input
                            type="text"
                            value={contributorName}
                            onChange={(e) => setContributorName(e.target.value)}
                            placeholder={t.namePlaceholder}
                            className="w-full border border-gray-300 rounded-lg px-4 py-2 mb-6 focus:outline-none focus:ring-2 focus:ring-gray-400 font-serif"
                        />
                        <div className="flex justify-end space-x-4">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="px-5 py-2 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-100 transition-colors duration-300 font-serif"
                            >
                                {t.cancel}
                            </button>
                            <button
                                onClick={confirmContribution}
                                className="px-5 py-2 bg-gray-800 text-white rounded-full hover:bg-gray-700 transition-colors duration-300 font-serif"
                                disabled={!contributorName.trim() || contributionAmount <= 0}
                            >
                                {t.confirm}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* QR Code Modal */}
            {isQrModalOpen && selectedItemId && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md relative text-center">
                        {/* Decorative element */}
                        <div className="absolute top-0 right-0 w-16 h-16 opacity-20">
                            <img src="/images/flowers.svg" alt="" className="w-full h-full object-contain" />
                        </div>

                        <h3 className="text-xl font-serif text-gray-800 mb-4">
                            {t.contribute}
                        </h3>

                        {(() => {
                            const item = items.find(item => item.id === selectedItemId);
                            if (!item) return null;

                            return (
                                <>
                                    <p className="text-lg font-serif text-gray-700 mb-2">{item.name}</p>
                                    <p className="text-sm text-gray-600 mb-4">
                                        {`${t.currency} ${contributionAmount}`}
                                    </p>
                                </>
                            );
                        })()}

                        {/* Status message for contribution submission */}
                        {submitLoading && (
                            <div className="my-4 py-2 px-4 bg-blue-50 text-blue-700 rounded-lg">
                                <Loader className="animate-spin h-4 w-4 inline-block mr-2" />
                                {t.submittingContribution}
                            </div>
                        )}

                        {submitSuccess && (
                            <div className="my-4 py-2 px-4 bg-green-50 text-green-700 rounded-lg">
                                {t.contributionSuccess}
                            </div>
                        )}

                        {/* QR Code Display */}
                        <div className="flex justify-center my-6">
                            <div className="border-4 border-gray-200 rounded-lg p-1">
                                <div className="w-64 h-64 bg-white flex items-center justify-center">
                                    <img
                                        src="/images/revolut_qr_code.jpg"
                                        alt="Payment QR Code"
                                        className="w-48 h-48"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="text-sm text-gray-600 mb-6 space-y-3">
                            <p>
                                {t.scanQrCode}
                            </p>
                            <div className="border-t border-gray-200 pt-3">
                                <p className="font-medium mb-2">{t.dontHaveRevolut}</p>
                                <p className="mb-2">{t.usePaymentLink}</p>
                                <a
                                    href="https://revolut.me/gerazvan"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-block bg-gray-100 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    https://revolut.me/gerazvan
                                </a>
                                <p className="mt-3">
                                    {t.currencyChange}
                                </p>
                            </div>
                        </div>

                        <div className="flex justify-center">
                            <button
                                onClick={closeQrModal}
                                className="px-5 py-2 bg-gray-800 text-white rounded-full hover:bg-gray-700 transition-colors duration-300 font-serif"
                                disabled={submitLoading}
                            >
                                {submitSuccess ? t.done : t.done}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Registry;